import { useCallback, useEffect, useState } from "react";
import { ClipboardCheck, KeyRound, CheckCircle2, Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function AttendanceTab() {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [regs, setRegs] = useState([]);
  const [checked, setChecked] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/events").then((r) => {
      const open = r.data.filter((e) => e.status !== "Selesai");
      setEvents(open);
      if (open[0]) setEventId(open[0].id);
    }).catch(() => {});
  }, []);

  const loadRegs = useCallback((id) => api.get(`/events/${id}/registrations`).then((r) => setRegs(r.data)).catch(() => {}), []);
  useEffect(() => {
    if (eventId) { loadRegs(eventId); setChecked([]); }
  }, [eventId, loadRegs]);

  const event = events.find((e) => e.id === eventId);
  const pending = regs.filter((r) => !r.checked_in);
  const toggle = (id) => setChecked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const bulkCheckin = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/checkin/bulk", { event_id: eventId, registration_ids: checked });
      toast.success(`${data.checked_in} relawan berhasil check-in. Jam kontribusi tercatat & sertifikat aktif.`);
      loadRegs(eventId);
      setChecked([]);
    } catch {
      toast.error("Gagal memproses check-in.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="font-heading font-extrabold text-slate-900 flex items-center gap-2 mb-4">
          <ClipboardCheck className="w-5 h-5 text-emerald-600" /> Daftar Hadir Relawan (Check-in Manual)
        </h3>
        <label htmlFor="attendance-event" className="text-sm font-bold text-slate-700 block mb-2">Pilih Kegiatan</label>
        <Select value={eventId} onValueChange={setEventId}>
          <SelectTrigger id="attendance-event" data-testid="attendance-event-select" className="rounded-xl">
            <SelectValue placeholder="Pilih kegiatan..." />
          </SelectTrigger>
          <SelectContent>
            {events.map((e) => <SelectItem key={e.id} value={e.id}>{e.title}</SelectItem>)}
          </SelectContent>
        </Select>

        {regs.length === 0 ? (
          <div data-testid="attendance-empty" className="text-center py-12 text-slate-400 text-sm">
            <Inbox className="w-8 h-8 mx-auto mb-2" /> Belum ada relawan terdaftar untuk kegiatan ini.
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mt-5 mb-2">
              <button
                data-testid="attendance-select-all"
                onClick={() => setChecked(checked.length === pending.length ? [] : pending.map((r) => r.id))}
                className="text-xs font-bold text-emerald-700 hover:underline"
              >
                {checked.length === pending.length && pending.length > 0 ? "Batalkan semua" : "Pilih semua yang belum hadir"}
              </button>
              <span className="text-xs font-semibold text-slate-500">{checked.length} dipilih</span>
            </div>
            <ul className="divide-y divide-slate-100 border border-slate-100 rounded-xl overflow-hidden">
              {regs.map((r) => (
                <li key={r.id} data-testid={`attendance-row-${r.id}`} className="flex items-center gap-3 px-4 py-3 bg-white">
                  {r.checked_in ? (
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" aria-label="Sudah hadir" />
                  ) : (
                    <Checkbox
                      data-testid={`attendance-check-${r.id}`}
                      id={`att-${r.id}`}
                      checked={checked.includes(r.id)}
                      onCheckedChange={() => toggle(r.id)}
                      aria-label={`Tandai hadir: ${r.name}`}
                    />
                  )}
                  <label htmlFor={`att-${r.id}`} className="flex-1 font-semibold text-sm text-slate-800 cursor-pointer">{r.name}</label>
                  {r.checked_in && (
                    <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800">Hadir ✓</span>
                  )}
                </li>
              ))}
            </ul>
            <Button
              data-testid="bulk-checkin-btn"
              onClick={bulkCheckin}
              disabled={checked.length === 0 || submitting}
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold mt-4"
            >
              <ClipboardCheck className="w-4 h-4 mr-2" />
              {submitting ? "Memproses..." : `Check-in ${checked.length} Relawan Terpilih`}
            </Button>
          </>
        )}
      </div>

      <div className="bg-slate-900 rounded-2xl p-6 h-fit">
        <h3 className="font-heading font-extrabold text-white flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-emerald-400" /> Kode PIN Harian
        </h3>
        <p className="text-xs text-slate-400 mt-1 leading-relaxed">
          Bagikan kode ini secara lisan atau tulis di papan posko. Relawan memasukkannya di halaman Profil untuk mencatat kehadiran mandiri — tanpa kamera atau sinyal kuat.
        </p>
        <div data-testid="daily-pin-display" className="mt-4 bg-white/10 border-2 border-emerald-500/50 rounded-2xl py-5 text-center">
          <p className="font-heading font-black text-4xl tracking-[0.2em] text-emerald-300">
            {event?.checkin_code || "—"}
          </p>
        </div>
        <p className="text-[11px] text-slate-500 mt-3">Kode berlaku untuk: <span className="text-slate-300 font-semibold">{event?.title || "-"}</span></p>
      </div>
    </div>
  );
}
